import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListOrdered, Eye, FileX2, ShoppingBag, Wallet } from "lucide-react";
import EstadoPagoBadge from "@/components/EstadoPagoBadge";

const STYLES = {
  PENDIENTE: { bg: "bg-[#FDF3E1]", text: "text-[#A6751F]", border: "border-[#F0DEB2]", label: "Pendiente" },
  CONFIRMADO: { bg: "bg-[#E9EBE4]", text: "text-[#545848]", border: "border-[#D5D8CD]", label: "Confirmado" },
  ENTREGADO: { bg: "bg-[#DAE6D7]", text: "text-[#3F6B3C]", border: "border-[#BCD3B7]", label: "Entregado" },
  CANCELADO: { bg: "bg-[#FBEBEB]", text: "text-[#8E2E2E]", border: "border-[#F0CECE]", label: "Cancelado" },
};

function EstadoTag({ estado }) {
  const s = STYLES[estado] || STYLES.PENDIENTE;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.12em] border ${s.bg} ${s.text} ${s.border}`} data-testid={`estado-${estado}`}>
      {s.label}
    </span>
  );
}

export default function ClienteMisPedidos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cc, setCc] = useState({ deuda_total: 0, pedidos: [] });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/pedidos"),
      api.get("/cuenta-corriente/mia"),
    ])
      .then(([r, ccr]) => { setRows(r.data); setCc(ccr.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-2">Tu historial</div>
      <h1 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-2">Mis pedidos</h1>
      <p className="text-[#595959] text-sm mb-6 sm:mb-8">Ahí podés ver el estado y descargar el remito cuando esté confirmado.</p>

      {!loading && cc.deuda_total > 0 && (
        <Card className="border border-[#E8C9BB] rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-[#F4EAE6] to-[#FBF1ED] mb-5 flex items-center gap-4" data-testid="cli-deuda-banner">
          <div className="h-12 w-12 rounded-lg bg-white text-[#A64A29] flex items-center justify-center shrink-0 ring-1 ring-[#E8C9BB]">
            <Wallet className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A64A29] font-semibold">Tu deuda actual</div>
            <div className="font-display font-bold text-2xl sm:text-3xl text-[#A64A29] leading-tight" data-testid="cli-deuda-monto">
              {formatMoney(cc.deuda_total)}
            </div>
            <div className="text-[11px] sm:text-xs text-[#A64A29]/80 mt-0.5">
              {cc.pedidos.length} pedido{cc.pedidos.length === 1 ? "" : "s"} a cuenta corriente
            </div>
          </div>
        </Card>
      )}

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-[#E5E0D8] rounded-2xl p-4 h-24 animate-pulse bg-[#F4EAE6]/40" />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="border border-dashed border-[#E5E0D8] rounded-xl p-10 text-center bg-white">
          <FileX2 className="h-12 w-12 mx-auto text-[#C85A32] mb-3 opacity-60" />
          <div className="font-display font-semibold text-lg">Aún no hiciste pedidos</div>
          <p className="text-sm text-[#595959] mt-1 mb-4">Empezá explorando el catálogo.</p>
          <Link to="/cliente">
            <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2">
              <ShoppingBag className="h-4 w-4" /> Ir al catálogo
            </Button>
          </Link>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-3" data-testid="cli-pedidos-list">
          {rows.map((p, i) => (
            <Link
              key={p.id}
              to={`/cliente/pedidos/${p.id}`}
              className="block fade-row"
              style={{ animationDelay: `${i * 30}ms` }}
              data-testid={`cli-ped-row-${p.pedido_id}`}
            >
              <Card className="border-[#E5E0D8] rounded-2xl bg-white p-4 sm:p-5 hover:shadow-md transition-shadow flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-[#F4EAE6] text-[#C85A32] flex items-center justify-center shrink-0">
                  <ListOrdered className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-[#C85A32]">{p.pedido_id}</span>
                    <EstadoTag estado={p.estado} />
                    {p.estado_pago && p.estado_pago !== "PAGADO" && p.estado_pago !== "NO_PAGADO" && (
                      <EstadoPagoBadge estado={p.estado_pago} />
                    )}
                    {p.estado_pago === "PAGADO" && <EstadoPagoBadge estado="PAGADO" />}
                    <span className="text-[12px] text-[#8C8C8C]">{formatDate(p.fecha)}</span>
                  </div>
                  <div className="text-sm text-[#595959] mt-0.5">
                    <span className="font-mono font-semibold text-[#1A1A1A]">
                      {p.items.reduce((s, it) => s + Number(it.cantidad), 0)}
                    </span> u · {p.items.length} producto{p.items.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-lg text-[#1A1A1A]">{formatMoney(p.total)}</div>
                  <div className="text-[11px] text-[#C85A32] flex items-center gap-1 justify-end mt-0.5">
                    <Eye className="h-3 w-3" /> Ver detalle
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
